import { EnrollmentPaymentStatus, PayFor } from './GrooverTypes'

/**
 * Diagnostic data sent from frontend to help validate race condition fix.
 * This is temporary and should be removed after fix is validated in production.
 * See: docs/STRIPE_PAYMENT_RACE_CONDITION_FIX.md
 */
export type StripePaymentIntentDiagnostics = {
  /** Frontend version identifier for A/B comparison */
  clientVersion: string
  /** Payment status from each data source at time of request */
  paymentStatusSources: {
    enrollmentPublicMonitor: EnrollmentPaymentStatus | undefined
    obfuscatedEnrollment: EnrollmentPaymentStatus | undefined
    enrollment: EnrollmentPaymentStatus | undefined
  }
  /** Whether frontend refetched enrollment before calculating amount */
  didRefetchEnrollment: boolean
  /** Amount calculated by frontend before sending request */
  frontendCalculatedAmount: number
}

export type CreateStripePaymentIntentWebRequest = {
  enrollmentId: string
  payFor: PayFor
  /**
   * Optional enrollmentUserId from URL parameter (?u=).
   * When provided with hardCoupleIndividualParticipantFlow, this identifies
   * the actual user making the payment (overrides payFor-based user selection).
   */
  enrollmentUserId?: string
  /** Optional diagnostic data for validating race condition fix */
  _diagnostics?: StripePaymentIntentDiagnostics
}

export type CreateStripePaymentIntentResponse = {
  clientSecret: string
  amount: number
  currency: string
}

export type PaymentIntentCreateRequest = {
  amount: number
  currency: string
  clientId: string
  stripeCustomerId?: string
  enrollmentId: string
  paymentMethodTypes: string[]
  userId: string
  /**
   * Who this intent settles for, already resolved to `effectivePayFor` (i.e. `enrollmentUserId`
   * has been taken into account). Copied into the PaymentIntent metadata so the webhook can
   * address the payment-confirmed email without re-deriving it — `userId` alone is not enough,
   * since `PayFor.Both` also carries `mainUser.id` and would silently skip the partner.
   */
  payFor: PayFor
}

export enum StripePaymentIntentStatusValue {
  Processing = 'processing',
  Succeeded = 'succeeded',
  Failed = 'failed'
}

export enum StripePaymentIntentType {
  PrivateLessonOrder = 'privateLessonOrder',
  Dropin = 'dropin',
  Enrollment = 'enrollment',
  SmsTopup = 'sms_topup',
  WalkIn = 'walkIn'
}

export type StripePaymentIntentStatus = {
  id: string
  status: StripePaymentIntentStatusValue
  type: StripePaymentIntentType
  createdAt: string
  updatedAt: string
  errorMessage?: string
  /** Amount in major currency unit (e.g. SEK, not öre) */
  amount?: number
  /** ISO 4217 currency code in uppercase (e.g. "SEK") */
  currency?: string
  /** Stripe operator identifier (client.stripe.operator) */
  stripeOperator?: string
  /** Stripe environment ("test" | "production") */
  stripeEnvironment?: string
  /** Enrollment user ID (available for enrollment flow) */
  enrollmentUserId?: string
}

export type StripeEnvironment = 'test' | 'production'

export type StripeConfig = {
  secret: string
  /** Stripe webhook signing secret (whsec_…). When present, incoming webhook signatures are verified. */
  webhookSecret?: string
}

export type RefundableStripePayment = {
  paymentIntentId: string // original payment's paymentRef (pi_xxx)
  amount: number // remaining refundable, in öre/cents
  originalAmount: number // original payment amount, in öre/cents
  alreadyRefunded: number // already refunded, in öre/cents
  paymentInfo?: string // e.g. "card-visa-4242", for display so admin can tell payments apart
  userId?: string
}
